import { Component, OnInit, OnDestroy } from '@angular/core';
import { ArchiveDashboardService } from './archive-dashboard.service';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { LogViewerDialogComponent } from './log-viewer-dialog.component';
import { Sort } from '@angular/material/sort';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PerformanceMonitoringService, SitePerformanceResponse, CameraTestResponse } from 'src/app/services/performance-monitoring.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';

const ENV_OPTIONS = [
  { label: 'Test', value: 'test' },
  { label: 'UAT', value: 'uat' },
  { label: 'Prod', value: 'prod' }
];

@Component({
  selector: 'app-archive-dashboard',
  templateUrl: './archive-dashboard.component.html',
  styleUrls: ['./archive-dashboard.component.css']
,
  standalone: false
})
export class ArchiveDashboardComponent implements OnInit, OnDestroy {
  showDashboard = false;
  envOptions = ENV_OPTIONS;
  selectedEnv = 'uat';
  queueStatus: any = null;
  queueData: any = null;
  allArchives: any[] = [];
  allArchivesSortActive = '';
  allArchivesSortDirection: 'asc' | 'desc' | '' = '';
  // Pagination properties
  archivesPageIndex = 0;
  archivesPageSize = 20;
  totalArchives = 0;
  runningJobs: any[] = [];
  runningSortActive = '';
  runningSortDirection: 'asc' | 'desc' | '' = '';
  pendingJobs: any[] = [];
  loadingQueue = false;
  loadingArchives = false;
  loadingRunning = false;
  loadingPending = false;
  errorQueue = '';
  errorArchives = '';
  errorRunning = '';
  errorPending = '';
  cancelAllLoading = false;
  cancelAllPendingLoading = false;
  cancelJobLoading: { [archiveId: string]: boolean } = {};
  cancelAllError = '';
  cancelAllPendingError = '';
  cancelJobError: { [archiveId: string]: string } = {};
  rerunJobLoading: { [archiveId: string]: boolean } = {};
  rerunJobError: { [archiveId: string]: string } = {};
  deleteJobLoading: { [archiveId: string]: boolean } = {};
  deleteJobError: { [archiveId: string]: string } = {};
  isAdminUser = false;
  pendingJobsExpanded = true;
  selectedArchiveSource = '';
  selectedRunningSource = '';
  selectedPendingSource = '';
  includeDeleted = false;
  
  // Performance monitoring properties
  performanceLoading: { [archiveId: string]: boolean } = {};
  performanceError: { [archiveId: string]: string } = {};
  performanceData: { [archiveId: string]: { sitePerformance?: SitePerformanceResponse; cameraPerformances: CameraTestResponse[] } } = {};

  displayedColumns: string[] = [
    'archiveId', 'siteName', 'createdDateTime', 'fromDateTime', 'toDateTime', 'source', 'user', 'progressPercent', 'fileSizeReadable', 'readableProcessingTime', 'status', 'potentialId', 'cameraUniqIdList', 'performance', 'rerun', 'delete', 'download', 'logs', 'cancel'
  ];
  runningColumns: string[] = [
    'archiveId', 'siteName', 'fromDateTime', 'toDateTime', 'source', 'user', 'progressPercent', 'poolType', 'cameraUniqIdList', 'potentialId', 'readableRunningTime', 'readableDuration', 'readableProcessingTime', 'status', 'performance', 'rerun', 'cancel', 'logs'
  ];
  pendingColumns: string[] = [
    'archiveId', 'fromDateTime', 'toDateTime', 'source', 'user', 'progressPercent', 'status', 'createdDateTime', 'performance', 'rerun', 'cancel', 'logs'
  ];

  private autoReloadInterval: any;
  private archivesSort = 'createdDateTime,desc';

  constructor(
    private archiveService: ArchiveDashboardService, 
    private dialog: MatDialog, 
    private authService: AuthenticationService, 
    private snackBar: MatSnackBar,
    private performanceService: PerformanceMonitoringService
  ) {}

  ngOnInit() {
    const currentUser = this.authService.currentUserValue;
    this.isAdminUser = ArchiveDashboardService.isAdminUser(currentUser?.email);
    this.showDashboard = this.isAdminUser;
    this.archiveService.setEnv(this.selectedEnv);
    this.performanceService.setEnv(this.selectedEnv);
    console.log('Environment:', this.selectedEnv);
    this.fetchAllData();
    this.autoReloadInterval = setInterval(() => {
      this.fetchAllData();
    }, 60000);
  }

  ngOnDestroy() {
    if (this.autoReloadInterval) {
      clearInterval(this.autoReloadInterval);
    }
  }

  onEnvChange() {
    this.archiveService.setEnv(this.selectedEnv);
    this.performanceService.setEnv(this.selectedEnv);
    this.fetchAllData();
  }

  onArchiveSourceChange() {
    this.archivesPageIndex = 0;
    this.fetchAllArchives(this.archivesPageIndex, this.archivesPageSize, 'createdDateTime,desc');
  }

  onRunningSourceChange() {
    this.fetchRunningJobs();
  }

  onPendingSourceChange() {
    this.fetchPendingJobs();
  }

  onIncludeDeletedChange() {
    this.archivesPageIndex = 0;
    this.fetchAllArchives(this.archivesPageIndex, this.archivesPageSize, 'createdDateTime,desc');
  }

  fetchAllData() {
    this.fetchQueueStatus();
    this.fetchAllArchives(this.archivesPageIndex, this.archivesPageSize, 'createdDateTime,desc');
    this.fetchRunningJobs();
    this.fetchPendingJobs();
  }

  fetchQueueStatus() {
    this.loadingQueue = true;
    this.errorQueue = '';
    this.archiveService.getQueueStatus().subscribe({
      next: (res: any) => {
        this.queueStatus = res;
        this.queueData = res;
        this.loadingQueue = false;
        console.log('Queue Status:', this.queueStatus);
      },
      error: (err) => {
        this.errorQueue = 'Failed to load queue status.';
        this.loadingQueue = false;
      }
    });
  }

  fetchAllArchives(page = 0, size = 20, sort = 'createdDateTime,desc') {
    this.loadingArchives = true;
    this.errorArchives = '';
    this.archiveService.getAllArchives(page, size, sort, this.selectedArchiveSource || undefined, this.includeDeleted).subscribe({
      next: (res: any) => {
        console.log('All Archives Response:', res);
        this.allArchives = res.archives || [];
        this.totalArchives = res.totalCount || (res.archives ? res.archives.length : 0);
        console.log('Processed Archives:', this.allArchives.length, 'Total:', this.totalArchives);
        this.loadingArchives = false;
      },
      error: (err) => {
        console.error('All Archives Error:', err);
        this.errorArchives = 'Failed to load archives.';
        this.loadingArchives = false;
      }
    });
  }

  onArchivesPage(event: any) {
    this.archivesPageIndex = event.pageIndex;
    this.archivesPageSize = event.pageSize;
    this.loadingArchives = true;
    this.fetchAllArchives(this.archivesPageIndex, this.archivesPageSize, 'createdDateTime,desc');
  }

  fetchRunningJobs() {
    this.loadingRunning = true;
    this.errorRunning = '';
    this.archiveService.getRunningJobs(this.selectedRunningSource || undefined).subscribe({
      next: (res: any) => {
        console.log('Running Jobs Response:', res);
        this.runningJobs = res.runningJobs || [];
        console.log('Processed Running Jobs:', this.runningJobs.length);
        this.loadingRunning = false;
      },
      error: (err) => {
        console.error('Running Jobs Error:', err);
        this.errorRunning = 'Failed to load running jobs.';
        this.loadingRunning = false;
      }
    });
  }

  onRunningSort(event: Sort) {
    this.runningSortActive = event.active;
    this.runningSortDirection = event.direction as 'asc' | 'desc' | '';
    if (event.active === 'poolType' && event.direction) {
      this.runningJobs = [...this.runningJobs].sort((a, b) => {
        const aVal = a.poolType || '';
        const bVal = b.poolType || '';
        return event.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      });
    } else if (event.active === 'progressPercent' && event.direction) {
      this.runningJobs = [...this.runningJobs].sort((a, b) => {
        const aVal = a.progressPercent || 0;
        const bVal = b.progressPercent || 0;
        return event.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    } else if (event.active === 'readableRunningTime' && event.direction) {
      this.runningJobs = [...this.runningJobs].sort((a, b) => {
        const aVal = this.parseRunningTime(a.readableRunningTime);
        const bVal = this.parseRunningTime(b.readableRunningTime);
        return event.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    } else if (!event.direction) {
      // Optionally, reload or reset to original order if needed
      this.fetchRunningJobs();
    }
  }

  // Helper to parse time strings like '2h 5m 10s', '44s', '10m', etc. to seconds
  private parseRunningTime(time: string | undefined): number {
    if (!time) return 0;
    let total = 0;
    const h = /([0-9]+)h/.exec(time);
    const m = /([0-9]+)m/.exec(time);
    const s = /([0-9]+)s/.exec(time);
    if (h) total += parseInt(h[1], 10) * 3600;
    if (m) total += parseInt(m[1], 10) * 60;
    if (s) total += parseInt(s[1], 10);
    return total;
  }

  fetchPendingJobs() {
    this.loadingPending = true;
    this.errorPending = '';
    this.archiveService.getPendingJobs(this.selectedPendingSource || undefined).subscribe({
      next: (res: any) => {
        this.pendingJobs = res.pendingJobs || [];
        this.loadingPending = false;
      },
      error: (err) => {
        this.errorPending = 'Failed to load pending jobs.';
        this.loadingPending = false;
      }
    });
  }

  togglePendingJobs() {
    this.pendingJobsExpanded = !this.pendingJobsExpanded;
  }

  downloadLink(archive: any): string | null {
    return archive.archiveLocation || null;
  }

  copyToClipboard(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.snackBar.open('Download link copied!', 'Close', { duration: 2000 });
      });
    } else {
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.snackBar.open('Download link copied!', 'Close', { duration: 2000 });
    }
  }

  viewLogs(archiveId: string) {
    this.archiveService.getArchiveLogs(archiveId).subscribe({
      next: (res: any) => {
        this.dialog.open(LogViewerDialogComponent, {
          data: res,
          width: '800px',
          maxHeight: '80vh',
          autoFocus: false
        });
      },
      error: err => {
        this.dialog.open(LogViewerDialogComponent, {
          data: { error: 'Failed to load logs.' },
          width: '600px',
          maxHeight: '60vh',
          autoFocus: false
        });
      }
    });
  }

  cancelAllJobs() {
    this.cancelAllLoading = true;
    this.cancelAllError = '';
    this.archiveService.cancelAllJobs().subscribe({
      next: () => {
        this.cancelAllLoading = false;
        this.fetchRunningJobs();
        this.fetchPendingJobs();
        this.fetchAllArchives();
      },
      error: err => {
        this.cancelAllError = 'Failed to cancel all jobs.';
        this.cancelAllLoading = false;
      }
    });
  }

  cancelAllPendingJobs() {
    this.cancelAllPendingLoading = true;
    this.cancelAllPendingError = '';
    this.archiveService.cancelAllPendingJobs().subscribe({
      next: () => {
        this.cancelAllPendingLoading = false;
        this.fetchPendingJobs();
      },
      error: err => {
        this.cancelAllPendingError = 'Failed to cancel all pending jobs.';
        this.cancelAllPendingLoading = false;
      }
    });
  }

  cancelJob(archiveId: string) {
    this.cancelJobLoading[archiveId] = true;
    this.cancelJobError[archiveId] = '';
    this.archiveService.cancelJob(archiveId).subscribe({
      next: () => {
        this.cancelJobLoading[archiveId] = false;
        this.fetchRunningJobs();
        this.fetchPendingJobs();
        this.fetchAllArchives();
      },
      error: err => {
        this.cancelJobError[archiveId] = 'Failed to cancel job.';
        this.cancelJobLoading[archiveId] = false;
      }
    });
  }

  rerunJob(archiveId: string) {
    this.rerunJobLoading[archiveId] = true;
    this.rerunJobError[archiveId] = '';
    this.archiveService.rerunJob(archiveId).subscribe({
      next: () => {
        this.rerunJobLoading[archiveId] = false;
        this.snackBar.open('Job re-run initiated successfully', 'Close', { duration: 3000 });
        this.fetchAllData();
      },
      error: (err) => {
        this.rerunJobLoading[archiveId] = false;
        this.rerunJobError[archiveId] = 'Failed to re-run job.';
        this.snackBar.open('Failed to re-run job', 'Close', { duration: 3000 });
      }
    });
  }

  softDeleteJob(archiveId: string) {
    this.deleteJobLoading[archiveId] = true;
    this.deleteJobError[archiveId] = '';
    this.archiveService.softDeleteArchive(archiveId).subscribe({
      next: () => {
        this.deleteJobLoading[archiveId] = false;
        this.snackBar.open('Archive deleted successfully', 'Close', { duration: 3000 });
        this.fetchAllData();
      },
      error: (err) => {
        this.deleteJobLoading[archiveId] = false;
        this.deleteJobError[archiveId] = 'Failed to delete archive.';
        this.snackBar.open('Failed to delete archive', 'Close', { duration: 3000 });
      }
    });
  }

  confirmDeleteJob(archive: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Delete Archive',
        message: `Are you sure you want to delete this archive?`,
        details: {
          archiveId: archive.archiveId,
          user: archive.user,
          fromDateTime: archive.fromDateTime || archive.startTime,
          toDateTime: archive.toDateTime || archive.endTime,
          siteName: archive.siteName,
          status: archive.status,
          size: archive.fileSizeReadable
        },
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.softDeleteJob(archive.archiveId);
      }
    });
  }

  confirmCancelJob(job: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Cancel Job',
        message: `Are you sure you want to cancel this job?`,
        details: {
          archiveId: job.archiveId,
          user: job.user,
          fromDateTime: job.fromDateTime || job.startTime,
          toDateTime: job.toDateTime || job.endTime,
          siteName: job.siteName,
          status: job.status
        },
        confirmText: 'Cancel Job',
        cancelText: 'Keep Running',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancelJob(job.archiveId);
      }
    });
  }

  confirmCancelAllJobs() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Cancel All Jobs',
        message: `Are you sure you want to cancel all running jobs?`,
        details: {
          count: this.runningJobs.length,
          jobs: this.runningJobs.slice(0, 3).map(job => ({
            archiveId: job.archiveId,
            user: job.user,
            fromDateTime: job.fromDateTime || job.startTime,
            toDateTime: job.toDateTime || job.endTime
          }))
        },
        confirmText: 'Cancel All',
        cancelText: 'Keep Running',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancelAllJobs();
      }
    });
  }

  confirmRerunJob(archive: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Re-run Job',
        message: `Are you sure you want to re-run this job?`,
        details: {
          archiveId: archive.archiveId,
          user: archive.user,
          fromDateTime: archive.fromDateTime || archive.startTime,
          toDateTime: archive.toDateTime || archive.endTime,
          siteName: archive.siteName,
          status: archive.status
        },
        confirmText: 'Re-run',
        cancelText: 'Cancel',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.rerunJob(archive.archiveId);
      }
    });
  }

  openQueueDetailsModal() {
    this.dialog.open(QueueDetailsModalComponent, {
      data: this.queueData,
      width: '600px',
      maxHeight: '80vh',
      autoFocus: false
    });
  }

  onAllArchivesSort(event: Sort) {
    this.allArchivesSortActive = event.active;
    this.allArchivesSortDirection = event.direction as 'asc' | 'desc' | '';
    if (event.active === 'progressPercent' && event.direction) {
      this.allArchives = [...this.allArchives].sort((a, b) => {
        const aVal = a.progressPercent || 0;
        const bVal = b.progressPercent || 0;
        return event.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    } else if (!event.direction) {
      this.fetchAllArchives(this.archivesPageIndex, this.archivesPageSize, 'createdDateTime,desc');
    }
  }

  openCameraIdsDialog(cameraIds: string[], event: Event, cameraResults?: any[], status?: string, potentialId?: any, fromDateTime?: string, toDateTime?: string, user?: string) {
    event.preventDefault();
    this.dialog.open(CameraIdsDialogComponent, {
      data: { cameraIds, cameraResults, status, potentialId, fromDateTime, toDateTime, user },
      width: '600px',
      maxHeight: '60vh',
      autoFocus: false
    });
  }

  /**
   * Fetch performance data for a specific job
   */
  fetchJobPerformance(job: any) {
    if (!job.potentialId) {
      this.snackBar.open('No potential ID available for this job', 'Close', { duration: 3000 });
      return;
    }

    this.performanceLoading[job.archiveId] = true;
    this.performanceError[job.archiveId] = '';

    this.performanceService.getJobPerformanceData(job).subscribe({
      next: (data) => {
        this.performanceData[job.archiveId] = data;
        this.performanceLoading[job.archiveId] = false;
        this.snackBar.open('Performance data loaded successfully', 'Close', { duration: 2000 });
      },
      error: (error) => {
        this.performanceError[job.archiveId] = 'Failed to load performance data';
        this.performanceLoading[job.archiveId] = false;
        console.error('Error fetching performance data:', error);
        this.snackBar.open('Failed to load performance data', 'Close', { duration: 3000 });
      }
    });
  }

  /**
   * Open performance details dialog
   */
  openPerformanceDialog(job: any) {
    if (this.performanceLoading[job.archiveId]) {
      this.snackBar.open('Loading performance data...', 'Close', { duration: 2000 });
      return;
    }

    if (!this.performanceData[job.archiveId]) {
      this.fetchJobPerformance(job);
      return;
    }

    const dialogRef = this.dialog.open(PerformanceDetailsDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '80%',
      disableClose: false,
      data: {
        job: job,
        performanceData: this.performanceData[job.archiveId]
      }
    });
  }

  /**
   * Get performance status color for display
   */
  getPerformanceStatusColor(performanceData: any): string {
    if (!performanceData) return 'gray';
    
    // Check site performance
    if (performanceData.sitePerformance && !performanceData.sitePerformance.success) {
      return 'red';
    }
    
    // Check camera performances
    if (performanceData.cameraPerformances && performanceData.cameraPerformances.length > 0) {
      const hasErrors = performanceData.cameraPerformances.some((cam: any) => !cam.success);
      if (hasErrors) return 'orange';
    }
    
    return 'green';
  }

  /**
   * Get performance status text for display
   */
  getPerformanceStatusText(performanceData: any): string {
    if (!performanceData) return 'Not Loaded';
    
    if (performanceData.sitePerformance && !performanceData.sitePerformance.success) {
      return 'Site Error';
    }
    
    if (performanceData.cameraPerformances && performanceData.cameraPerformances.length > 0) {
      const errorCount = performanceData.cameraPerformances.filter((cam: any) => !cam.success).length;
      const totalCount = performanceData.cameraPerformances.length;
      
      if (errorCount === 0) return 'All Good';
      if (errorCount === totalCount) return 'All Failed';
      return `${errorCount}/${totalCount} Failed`;
    }
    
    return 'No Cameras';
  }
}

// Modal component for queue details
@Component({
  selector: 'app-queue-details-modal',
  template: `
    <h2 mat-dialog-title>Queue Details</h2>
    <mat-dialog-content style="max-height:60vh;overflow:auto;">
      <div *ngIf="data">
        <div style="margin-bottom: 16px;">
          <b>Queue Details:</b>
          <ul>
            <li>Total Queued: {{data.queueDetails?.totalQueued}}</li>
            <li>Available Slots: {{data.queueDetails?.availableSlots}}</li>
            <li>Total Capacity: {{data.queueDetails?.totalCapacity}}</li>
            <li>Short Duration Pool: Capacity {{data.queueDetails?.shortDurationPool?.capacity}}, Queued {{data.queueDetails?.shortDurationPool?.queued}}, Max Threads {{data.queueDetails?.shortDurationPool?.maxThreads}}, Active {{data.queueDetails?.shortDurationPool?.active}}</li>
            <li>Long Duration Pool: Capacity {{data.queueDetails?.longDurationPool?.capacity}}, Queued {{data.queueDetails?.longDurationPool?.queued}}, Max Threads {{data.queueDetails?.longDurationPool?.maxThreads}}, Active {{data.queueDetails?.longDurationPool?.active}}</li>
          </ul>
        </div>
        <div style="margin-bottom: 16px;">
          <b>Job Breakdown:</b>
          <ul>
            <li>Short Duration Running: {{data.jobBreakdown?.shortDurationRunning}}</li>
            <li>Long Duration Running: {{data.jobBreakdown?.longDurationRunning}}</li>
            <li>HD Running Jobs: {{data.jobBreakdown?.hdRunningJobs}}</li>
            <li>SD Running Jobs: {{data.jobBreakdown?.sdRunningJobs}}</li>
            <li>Total Pending Jobs: {{data.jobBreakdown?.totalPendingJobs}}</li>
            <li>Total Running Jobs: {{data.jobBreakdown?.totalRunningJobs}}</li>
          </ul>
        </div>
        <div style="margin-bottom: 16px;">
          <b>Wait Time Estimates:</b>
          <ul>
            <li>Average Wait Time: {{data.waitTimeEstimates?.averageWaitTime}}</li>
            <li>Max Wait Time: {{data.waitTimeEstimates?.maxWaitTime}}</li>
          </ul>
        </div>
        <div style="margin-bottom: 16px;">
          <b>Summary:</b>
          <ul>
            <li>Queue Health: {{data.summary?.queueHealth}}</li>
            <li>Queue Utilization: {{data.summary?.queueUtilization}}</li>
            <li>Estimated Wait Time: {{data.summary?.estimatedWaitTime}}</li>
            <li>Max Wait Time: {{data.summary?.maxWaitTime}}</li>
            <li>Total Pending Jobs: {{data.summary?.totalPendingJobs}}</li>
            <li>Total Running Jobs: {{data.summary?.totalRunningJobs}}</li>
          </ul>
        </div>
        <div style="margin-bottom: 16px;">
          <b>Timestamp:</b> {{data.timestamp}}
        </div>
        <details>
          <summary style="cursor:pointer;">Raw JSON</summary>
          <pre style="font-size:13px;white-space:pre-wrap;word-break:break-all;">{{ data | json }}</pre>
        </details>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  standalone: false
})
export class QueueDetailsModalComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
}

@Component({
  selector: 'app-camera-ids-dialog',
  template: `
    <h2 mat-dialog-title>
      Camera IDs <span style="font-weight:normal; font-size:15px; color:#1976d2;">({{data.cameraIds?.length || 0}})</span>
      <button mat-icon-button (click)="copyIds()" matTooltip="Copy all IDs" style="margin-left:8px;vertical-align:middle;">
        <mat-icon>content_copy</mat-icon>
      </button>
      <button mat-icon-button (click)="copyFullJson()" matTooltip="Copy full JSON" style="margin-left:4px;vertical-align:middle;">
        <mat-icon>code</mat-icon>
      </button>
    </h2>
    <mat-dialog-content style="max-height:50vh;overflow:auto;">
      <ng-container *ngIf="data.status === 'Running' || !data.cameraResults || !data.cameraResults.length; else detailsTable">
        <ul style="padding-left:18px;">
          <li *ngFor="let id of data.cameraIds">{{id}}</li>
        </ul>
      </ng-container>
      <ng-template #detailsTable>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="text-align:left;padding:4px 8px;">Camera ID</th>
              <th style="text-align:left;padding:4px 8px;">Name</th>
              <th style="text-align:left;padding:4px 8px;">File Size</th>
              <th style="text-align:left;padding:4px 8px;">Message</th>
              <th style="text-align:left;padding:4px 8px;">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let cam of data.cameraResults">
              <td style="padding:4px 8px;">{{cam.cameraUniqId}}</td>
              <td style="padding:4px 8px;">{{cam.camName}}</td>
              <td style="padding:4px 8px;">{{cam.fileSizeReadable}}</td>
              <td style="padding:4px 8px;">{{cam.message}}</td>
              <td style="padding:4px 8px;">{{cam.status}}</td>
            </tr>
          </tbody>
        </table>
      </ng-template>
      <div *ngIf="copiedJson" style="color:green;margin-top:8px;">Full JSON copied!</div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  standalone: false
})
export class CameraIdsDialogComponent {
  copied = false;
  copiedJson = false;
  constructor(@Inject(MAT_DIALOG_DATA) public data: { cameraIds: string[], cameraResults?: any[], status?: string, potentialId?: any, fromDateTime?: string, toDateTime?: string, user?: string }) {}
  copyIds() {
    const ids = (this.data.cameraIds || []).join(', ');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(ids).then(() => {
        this.copied = true;
        setTimeout(() => (this.copied = false), 1500);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = ids;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    }
  }
  copyFullJson() {
    const json = {
      potentialid: this.data.potentialId,
      cameraUniqIdList: this.data.cameraIds,
      fromDateTime: this.data.fromDateTime,
      toDateTime: this.data.toDateTime,
      user: this.data.user
    };
    const text = JSON.stringify(json, null, 4);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.copiedJson = true;
        setTimeout(() => (this.copiedJson = false), 1500);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copiedJson = true;
      setTimeout(() => (this.copiedJson = false), 1500);
    }
  }
}

// Confirmation Dialog Component
@Component({
  selector: 'app-confirmation-dialog',
  template: `
    <h2 mat-dialog-title>{{data.title}}</h2>
    <mat-dialog-content>
      <p>{{data.message}}</p>
      <div style="margin-top: 16px; padding: 12px; background: #f5f5f5; border-radius: 4px;">
        <div *ngIf="data.details.archiveId">
          <strong>Archive ID:</strong> {{data.details.archiveId}}
        </div>
        <div *ngIf="data.details.user">
          <strong>User:</strong> {{data.details.user}}
        </div>
        <div *ngIf="data.details.siteName">
          <strong>Site:</strong> {{data.details.siteName}}
        </div>
        <div *ngIf="data.details.fromDateTime">
          <strong>From:</strong> {{data.details.fromDateTime}}
        </div>
        <div *ngIf="data.details.toDateTime">
          <strong>To:</strong> {{data.details.toDateTime}}
        </div>
        <div *ngIf="data.details.status">
          <strong>Status:</strong> {{data.details.status}}
        </div>
        <div *ngIf="data.details?.size">
          <strong>Size:</strong> {{data.details?.size}}
        </div>
        <div *ngIf="data.details.count">
          <strong>Total Jobs:</strong> {{data.details.count}}
        </div>
        <div *ngIf="data.details.jobs && data.details.jobs.length > 0">
          <strong>Sample Jobs:</strong>
          <ul style="margin: 8px 0; padding-left: 20px;">
            <li *ngFor="let job of data.details.jobs">
              {{job.archiveId}} ({{job.user}}) - {{job.fromDateTime}} to {{job.toDateTime}}
            </li>
          </ul>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">{{data.cancelText}}</button>
      <button mat-raised-button [color]="data.confirmColor" [mat-dialog-close]="true">{{data.confirmText}}</button>
    </mat-dialog-actions>
  `,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule
  ]
})
export class ConfirmationDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: {
    title: string;
    message: string;
    details: any;
    confirmText: string;
    cancelText: string;
    confirmColor: string;
  }) {}
}

// Performance Details Dialog Component
@Component({
  selector: 'app-performance-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    FormsModule
  ],
  template: `
    <h2 mat-dialog-title>
      Performance Details - {{data.job.archiveId}}
      <button mat-icon-button mat-dialog-close style="float: right;">
        <mat-icon>close</mat-icon>
      </button>
    </h2>
    <mat-dialog-content style="max-height: 70vh; overflow: auto;">
      <div style="margin-bottom: 20px;">
        <h3>Job Information</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
          <div><strong>Archive ID:</strong> {{data.job.archiveId}}</div>
          <div><strong>Site:</strong> {{data.job.siteName || 'N/A'}}</div>
          <div><strong>Potential ID:</strong> {{data.job.potentialId || 'N/A'}}</div>
          <div><strong>Status:</strong> {{data.job.status}}</div>
          <div><strong>From:</strong> {{data.job.fromDateTime}}</div>
          <div><strong>To:</strong> {{data.job.toDateTime}}</div>
        </div>
      </div>

      <!-- Site Performance Section -->
      <div *ngIf="data.performanceData.sitePerformance" style="margin-bottom: 20px;">
        <h3>Site Performance</h3>
        <mat-card>
          <mat-card-content>
            <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
              <mat-icon [style.color]="data.performanceData.sitePerformance.success ? 'green' : 'red'">
                {{data.performanceData.sitePerformance.success ? 'check_circle' : 'error'}}
              </mat-icon>
              <span><strong>Status:</strong> {{data.performanceData.sitePerformance.success ? 'Success' : 'Failed'}}</span>
            </div>
            <div *ngIf="data.performanceData.sitePerformance.messages && data.performanceData.sitePerformance.messages.length > 0">
              <strong>Messages:</strong>
              <ul>
                <li *ngFor="let message of data.performanceData.sitePerformance.messages">{{message}}</li>
              </ul>
            </div>
            <div *ngIf="data.performanceData.sitePerformance.downloadTimeMs">
              <strong>Download Time:</strong> {{data.performanceData.sitePerformance.downloadTimeMs}}ms
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Camera Performance Section -->
      <div *ngIf="data.performanceData.cameraPerformances && data.performanceData.cameraPerformances.length > 0">
        <h3>Camera Performance ({{data.performanceData.cameraPerformances.length}} cameras)</h3>
        <div style="display: grid; gap: 15px;">
          <mat-card *ngFor="let camera of data.performanceData.cameraPerformances; let i = index">
            <mat-card-header>
              <mat-card-title>
                <div style="display: flex; align-items: center; gap: 10px;">
                  <mat-icon [style.color]="camera.testResults.assessment.overallHealth === 'HEALTHY' ? 'green' : 'red'">
                    {{camera.testResults.assessment.overallHealth === 'HEALTHY' ? 'check_circle' : 'error'}}
                  </mat-icon>
                  {{camera.cameraName || camera.cameraUniqId || 'Camera ' + (i + 1)}}
                </div>
              </mat-card-title>
              <mat-card-subtitle>
                <strong>Status:</strong> {{camera.success ? 'Success' : 'Failed'}}
                <span *ngIf="camera.testResults?.assessment?.overallHealth">
                  | <strong>Health:</strong> {{camera.testResults.assessment.overallHealth}}
                </span>
              </mat-card-subtitle>
            </mat-card-header>
            <mat-card-content *ngIf="camera.testResults">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                <div><strong>Device ID:</strong> {{camera.testResults.deviceId || 'N/A'}}</div>
                <div><strong>IP Address:</strong> {{camera.testResults.ipAddress || 'N/A'}}</div>
                <div><strong>Port:</strong> {{camera.testResults.port || 'N/A'}}</div>
                <div><strong>Camera Type:</strong> {{camera.testResults.cameraType || 'N/A'}}</div>
                <div><strong>Installed Status:</strong> {{camera.testResults.installedStatus || 'N/A'}}</div>
                <div><strong>Has Credentials:</strong> {{camera.testResults.hasCredentials ? 'Yes' : 'No'}}</div>
              </div>

              <!-- Camera Access Section -->
              <div style="margin-top: 15px; padding: 10px; background-color: #f5f5f5; border-radius: 4px;">
                <h4 style="margin-top: 0;">Camera Access</h4>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
                  <div><strong>Username:</strong> {{camera.testResults.connectivityTests?.authentication?.hasUsername ? 'Available' : 'Not Available'}}</div>
                  <div><strong>Password:</strong> {{camera.testResults.connectivityTests?.authentication?.hasPassword ? 'Available' : 'Not Available'}}</div>
                </div>
                <!-- Display actual credentials if available -->
                <div *ngIf="camera.testResults.connectivityTests?.authentication?.hasUsername && camera.testResults.connectivityTests?.authentication?.hasPassword" 
                     style="margin-bottom: 10px; padding: 8px; background-color: #e8f5e8; border: 1px solid #4caf50; border-radius: 4px;">
                  <div style="font-size: 12px; color: #2e7d32; margin-bottom: 4px;"><strong>Credentials Available:</strong></div>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                    <div><strong>Username:</strong> <span style="font-family: monospace;">{{camera.testResults.connectivityTests?.authentication?.username || 'admin'}}</span></div>
                    <div><strong>Password:</strong> <span style="font-family: monospace;">{{camera.testResults.connectivityTests?.authentication?.password || 'password'}}</span></div>
                  </div>
                </div>
                <div *ngIf="camera.testResults.connectivityTests?.authentication?.hasUsername && camera.testResults.connectivityTests?.authentication?.hasPassword" 
                     style="margin-bottom: 10px; padding: 8px; background-color: #e8f5e8; border: 1px solid #4caf50; border-radius: 4px;">
                  <div style="font-size: 12px; color: #2e7d32; margin-bottom: 4px;"><strong>Credentials Available:</strong></div>
                  <div style="font-size: 11px; color: #2e7d32;">
                    The camera has authentication credentials configured. You can use the Basic Auth URL format below.
                  </div>
                </div>
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
                  <button mat-raised-button 
                          color="primary" 
                          (click)="openCameraAccess(camera.testResults.ipAddress, camera.testResults.port)"
                          [disabled]="!camera.testResults.ipAddress || !camera.testResults.port"
                          matTooltip="Open camera in new tab">
                    <mat-icon>open_in_new</mat-icon>
                    Open Camera
                  </button>
                  <button mat-raised-button 
                          color="accent" 
                          (click)="copyCameraUrl(camera.testResults.ipAddress, camera.testResults.port)"
                          [disabled]="!camera.testResults.ipAddress || !camera.testResults.port"
                          matTooltip="Copy camera URL">
                    <mat-icon>content_copy</mat-icon>
                    Copy URL
                  </button>
                  <button mat-raised-button 
                          color="warn" 
                          (click)="openCameraWithAuth(camera.testResults.ipAddress, camera.testResults.port, camera.testResults.connectivityTests?.authentication?.username, camera.testResults.connectivityTests?.authentication?.password)"
                          [disabled]="!camera.testResults.ipAddress || !camera.testResults.port"
                          matTooltip="Open camera with basic auth">
                    <mat-icon>security</mat-icon>
                    Open with Auth
                  </button>
                </div>
                <div style="background-color: #fff; padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
                  <div style="font-size: 12px; color: #666; margin-bottom: 4px;"><strong>Camera URL:</strong></div>
                  <div style="font-family: monospace; font-size: 11px; word-break: break-all;">
                    http://{{camera.testResults.ipAddress}}:{{camera.testResults.port}}/
                  </div>
                </div>
                <div style="margin-top: 10px; padding: 8px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;">
                  <div style="font-size: 12px; color: #856404;">
                    <strong>Note:</strong> If the camera requires authentication, you'll be prompted for username and password when accessing the URL.
                  </div>
                </div>
                
                <!-- Basic Auth URL Section -->
                <div style="margin-top: 10px;">
                  <div style="font-size: 12px; color: #666; margin-bottom: 4px;"><strong>Basic Auth URL:</strong></div>
                  <div style="background-color: #fff; padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
                    <div style="font-family: monospace; font-size: 11px; word-break: break-all; margin-bottom: 4px;">
                      <ng-container *ngIf="camera.testResults.connectivityTests?.authentication?.hasUsername && camera.testResults.connectivityTests?.authentication?.hasPassword; else templateUrl">
                        http://{{camera.testResults.connectivityTests?.authentication?.username || 'admin'}}:{{camera.testResults.connectivityTests?.authentication?.password || '****'}}{{ '@' }}{{camera.testResults.ipAddress}}:{{camera.testResults.port}}/
                      </ng-container>
                      <ng-template #templateUrl>
                        http://username:password{{ '@' }}{{camera.testResults.ipAddress}}:{{camera.testResults.port}}/
                      </ng-template>
                    </div>
                    <button mat-button 
                            color="primary" 
                            (click)="copyBasicAuthUrl(camera.testResults.ipAddress, camera.testResults.port, camera.testResults.connectivityTests?.authentication?.username, camera.testResults.connectivityTests?.authentication?.password)"
                            [disabled]="!camera.testResults.ipAddress || !camera.testResults.port"
                            style="font-size: 11px; padding: 2px 8px;">
                      <mat-icon style="font-size: 14px;">content_copy</mat-icon>
                      Copy Basic Auth URL
                    </button>
                  </div>
                  <div style="font-size: 10px; color: #999; margin-top: 4px;">
                    <ng-container *ngIf="camera.testResults.connectivityTests?.authentication?.hasUsername && camera.testResults.connectivityTests?.authentication?.hasPassword">
                      URL includes actual credentials
                    </ng-container>
                    <ng-container *ngIf="!camera.testResults.connectivityTests?.authentication?.hasUsername || !camera.testResults.connectivityTests?.authentication?.hasPassword">
                      Replace "username:password" with actual credentials
                    </ng-container>
                  </div>
                </div>
              </div>

              <!-- Connectivity Tests -->
              <div *ngIf="camera.testResults.connectivityTests" style="margin-top: 15px;">
                <h4>Connectivity Tests</h4>
                <div style="display: grid; gap: 10px;">
                  <!-- HTTP Connectivity -->
                  <div *ngIf="camera.testResults.connectivityTests.httpConnectivity">
                    <strong>HTTP Connectivity:</strong>
                    <div style="margin-left: 15px;">
                      <div>Status: {{camera.testResults.connectivityTests.httpConnectivity.status}}</div>
                      <div>Response Time: {{camera.testResults.connectivityTests.httpConnectivity.responseTimeMs}}ms</div>
                      <div>Message: {{camera.testResults.connectivityTests.httpConnectivity.message}}</div>
                    </div>
                  </div>

                  <!-- Socket Connectivity -->
                  <div *ngIf="camera.testResults.connectivityTests.socketConnectivity">
                    <strong>Socket Connectivity:</strong>
                    <div style="margin-left: 15px;">
                      <div>Status: {{camera.testResults.connectivityTests.socketConnectivity.status}}</div>
                      <div>Response Time: {{camera.testResults.connectivityTests.socketConnectivity.responseTimeMs}}ms</div>
                      <div>Message: {{camera.testResults.connectivityTests.socketConnectivity.message}}</div>
                    </div>
                  </div>

                  <!-- Authentication -->
                  <div *ngIf="camera.testResults.connectivityTests.authentication">
                    <strong>Authentication:</strong>
                    <div style="margin-left: 15px;">
                      <div>Status: {{camera.testResults.connectivityTests.authentication.status}}</div>
                      <div>Message: {{camera.testResults.connectivityTests.authentication.message}}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recommendations -->
              <div *ngIf="camera.testResults.recommendations && camera.testResults.recommendations.length > 0" style="margin-top: 15px;">
                <h4>Recommendations</h4>
                <ul>
                  <li *ngFor="let rec of camera.testResults.recommendations">{{rec}}</li>
                </ul>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <div *ngIf="!data.performanceData.sitePerformance && (!data.performanceData.cameraPerformances || data.performanceData.cameraPerformances.length === 0)" style="text-align: center; color: #666; margin-top: 20px;">
        No performance data available for this job.
      </div>

      <!-- Special Camera Access Section -->
      <div style="margin-top: 20px; padding: 15px; background-color: #e3f2fd; border: 1px solid #2196f3; border-radius: 4px;">
        <h3 style="margin-top: 0; color: #1976d2;">Quick Camera Access</h3>
        
        <!-- Camera Credentials Input -->
        <div style="margin-bottom: 15px; padding: 10px; background-color: #fff; border-radius: 4px; border: 1px solid #ddd;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <div style="font-size: 12px; color: #666;"><strong>Camera Credentials:</strong></div>
            <button mat-button 
                    color="primary" 
                    (click)="autoPopulateCredentials()"
                    style="font-size: 10px; padding: 2px 8px; min-width: auto;">
              <mat-icon style="font-size: 14px;">refresh</mat-icon>
              Auto-fill
            </button>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <div>
              <label style="font-size: 11px; color: #666;">Username:</label>
              <input type="text" 
                     [(ngModel)]="specificCameraUsername" 
                     placeholder="admin" 
                     style="width: 100%; padding: 4px; border: 1px solid #ccc; border-radius: 2px; font-size: 11px;">
            </div>
            <div>
              <label style="font-size: 11px; color: #666;">Password:</label>
              <input type="text" 
                     [(ngModel)]="specificCameraPassword" 
                     placeholder="password" 
                     style="width: 100%; padding: 4px; border: 1px solid #ccc; border-radius: 2px; font-size: 11px;">
            </div>
          </div>
        </div>

        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
          <button mat-raised-button 
                  color="primary" 
                  (click)="openSpecificCamera()"
                  matTooltip="Open specific camera">
            <mat-icon>videocam</mat-icon>
            Open Camera
          </button>
          <button mat-raised-button 
                  color="warn" 
                  (click)="openSpecificCameraWithAuth()"
                  matTooltip="Open camera with basic auth">
            <mat-icon>security</mat-icon>
            Open with Auth
          </button>
          <button mat-raised-button 
                  color="accent" 
                  (click)="copySpecificCameraUrl()"
                  matTooltip="Copy camera URL">
            <mat-icon>content_copy</mat-icon>
            Copy URL
          </button>
          <button mat-raised-button 
                  color="accent" 
                  (click)="copySpecificCameraAuthUrl()"
                  matTooltip="Copy camera URL with auth">
            <mat-icon>content_copy</mat-icon>
            Copy Auth URL
          </button>
        </div>
        
        <div style="background-color: #fff; padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
          <div style="font-size: 12px; color: #666; margin-bottom: 4px;"><strong>Camera URL:</strong></div>
          <div style="font-family: monospace; font-size: 11px; word-break: break-all; margin-bottom: 4px;">
            http://166.145.200.110:10000/
          </div>
          <div *ngIf="specificCameraUsername && specificCameraPassword" style="font-family: monospace; font-size: 11px; word-break: break-all; color: #2196f3;">
            http://{{specificCameraUsername}}:{{specificCameraPassword}}{{ '@' }}166.145.200.110:10000/
          </div>
        </div>
        <div style="margin-top: 10px; padding: 8px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;">
          <div style="font-size: 12px; color: #856404;">
            <strong>Note:</strong> Basic auth URL will open camera without prompting for credentials
          </div>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `
})
export class PerformanceDetailsDialogComponent {
  specificCameraUsername: string = '';
  specificCameraPassword: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      job: any;
      performanceData: { sitePerformance?: any; cameraPerformances: any[] };
    },
    public dialogRef: MatDialogRef<PerformanceDetailsDialogComponent>
  ) {
    // Auto-populate credentials from first camera if available
    this.autoPopulateCredentials();
  }

  autoPopulateCredentials() {
    if (this.data.performanceData?.cameraPerformances?.length > 0) {
      const firstCamera = this.data.performanceData.cameraPerformances[0];
      if (firstCamera.testResults?.connectivityTests?.authentication?.username) {
        this.specificCameraUsername = firstCamera.testResults.connectivityTests.authentication.username;
      }
      if (firstCamera.testResults?.connectivityTests?.authentication?.password) {
        this.specificCameraPassword = firstCamera.testResults.connectivityTests.authentication.password;
      }
    }
  }

  openCameraAccess(ipAddress: string, port: string) {
    if (ipAddress && port) {
      const cameraUrl = `http://${ipAddress}:${port}/`;
      window.open(cameraUrl, '_blank');
    }
  }

  openCameraWithAuth(ipAddress: string, port: string, username?: string, password?: string) {
    if (ipAddress && port) {
      let cameraUrl = `http://${ipAddress}:${port}/`;
      
      // If credentials are provided, create a basic auth URL that will open without prompting
      if (username && password) {
        // Encode username and password for URL safety
        const encodedUsername = encodeURIComponent(username);
        const encodedPassword = encodeURIComponent(password);
        cameraUrl = `http://${encodedUsername}:${encodedPassword}@${ipAddress}:${port}/`;
      }
      
      window.open(cameraUrl, '_blank');
    }
  }

  // Method to create a basic auth URL with placeholder credentials
  createBasicAuthUrl(ipAddress: string, port: string): string {
    return `http://username:password@${ipAddress}:${port}/`;
  }

  // Method to open camera with specific credentials (for the mentioned IP)
  openSpecificCamera() {
    // For the specific camera mentioned: http://166.145.200.110:10000/
    const cameraUrl = 'http://166.145.200.110:10000/';
    window.open(cameraUrl, '_blank');
  }

  copySpecificCameraUrl() {
    const cameraUrl = 'http://166.145.200.110:10000/';
    navigator.clipboard.writeText(cameraUrl).then(() => {
      console.log('Specific camera URL copied to clipboard:', cameraUrl);
    }).catch(err => {
      console.error('Failed to copy specific camera URL:', err);
    });
  }

  openSpecificCameraWithAuth() {
    if (this.specificCameraUsername && this.specificCameraPassword) {
      // Encode username and password for URL safety
      const encodedUsername = encodeURIComponent(this.specificCameraUsername);
      const encodedPassword = encodeURIComponent(this.specificCameraPassword);
      const cameraUrl = `http://${encodedUsername}:${encodedPassword}@166.145.200.110:10000/`;
      window.open(cameraUrl, '_blank');
    } else {
      // Fallback to regular URL if no credentials provided
      this.openSpecificCamera();
    }
  }

  copySpecificCameraAuthUrl() {
    if (this.specificCameraUsername && this.specificCameraPassword) {
      // Encode username and password for URL safety
      const encodedUsername = encodeURIComponent(this.specificCameraUsername);
      const encodedPassword = encodeURIComponent(this.specificCameraPassword);
      const cameraUrl = `http://${encodedUsername}:${encodedPassword}@166.145.200.110:10000/`;
      navigator.clipboard.writeText(cameraUrl).then(() => {
        console.log('Specific camera auth URL copied to clipboard:', cameraUrl);
      }).catch(err => {
        console.error('Failed to copy specific camera auth URL:', err);
      });
    } else {
      // Fallback to regular URL if no credentials provided
      this.copySpecificCameraUrl();
    }
  }

  copyCameraUrl(ipAddress: string, port: string) {
    if (ipAddress && port) {
      const cameraUrl = `http://${ipAddress}:${port}/`;
      navigator.clipboard.writeText(cameraUrl).then(() => {
        // You could add a toast notification here if you have a snackbar service
        console.log('Camera URL copied to clipboard:', cameraUrl);
      }).catch(err => {
        console.error('Failed to copy URL:', err);
      });
    }
  }

  copyBasicAuthUrl(ipAddress: string, port: string, username?: string, password?: string) {
    if (ipAddress && port) {
      let basicAuthUrl: string;
      
      if (username && password) {
        // Encode username and password for URL safety
        const encodedUsername = encodeURIComponent(username);
        const encodedPassword = encodeURIComponent(password);
        basicAuthUrl = `http://${encodedUsername}:${encodedPassword}@${ipAddress}:${port}/`;
      } else {
        basicAuthUrl = `http://username:password@${ipAddress}:${port}/`;
      }
      
      navigator.clipboard.writeText(basicAuthUrl).then(() => {
        console.log('Basic Auth URL copied to clipboard:', basicAuthUrl);
      }).catch(err => {
        console.error('Failed to copy Basic Auth URL:', err);
      });
    }
  }
} 